import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import AnacStudentPanel from "../components/anac/AnacStudentPanel";
import { ProximosVoos, InstrucaoPratica, DocumentosValidades } from "../components/student/PilotBlocks";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { BookOpen as HelpBook } from "lucide-react";
import { helpPath } from "../help";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import SkillRatingHistory from "./student/SkillRatingHistory";
import SelectSchoolDialog from "../components/SelectSchoolDialog";
import FlightPlanningPanel from "./student/FlightPlanningPanel";
import PushNotificationToggle from "../components/PushNotificationToggle";
import SinoPessoal from "../components/student/SinoPessoal";
import { 
  Plane, PlaneTakeoff, Target, Award, BookOpen, Clock, TrendingUp, 
  LogOut, ChevronRight, Play, Trophy, Flame, Star, Zap, Rocket, Eye,
  Shield, GraduationCap, LayoutDashboard, Lock, Bell, Medal, Crosshair,
  Calendar, CalendarCheck, CheckCircle, Moon, Sunrise, Users, Crown, AlertTriangle,
  CreditCard, Edit2, School, ChevronDown, BookMarked, FlaskConical
} from "lucide-react";

// Icon mapping for badges (same as BadgesPage)
const badgeIconMap = {
  Plane: Plane,
  PlaneTakeoff: Plane,
  Target: Target,
  Award: Award,
  Trophy: Trophy,
  Star: Star,
  Flame: Flame,
  Calendar: Calendar,
  CalendarCheck: CalendarCheck,
  Medal: Medal,
  Crosshair: Crosshair,
  CheckCircle: CheckCircle,
  Moon: Moon,
  Sunrise: Sunrise,
};

// Requirement text mapping for badges (explains why the badge was earned)
const badgeRequirementText = {
  "first_session": "Completou a sua primeira sessão de treino",
  "first_exam": "Completou o seu primeiro exame",
  "correct_50": "Acertou 50 respostas no total",
  "correct_100": "Acertou 100 respostas no total",
  "correct_500": "Acertou 500 respostas no total",
  "streak_3": "Treinou 3 dias consecutivos",
  "streak_7": "Treinou 7 dias consecutivos",
  "streak_30": "Treinou 30 dias consecutivos",
  "perfect_session": "Completou uma sessão com 100% de acerto",
  "exam_passed": "Passou num exame (75%+)",
  "night_owl": "Treinou depois da meia-noite",
  "early_bird": "Treinou antes das 6h da manhã",
  "first_login": "Completou o registo na plataforma",
};
import { useExamPolling } from "../hooks/useExamPolling";

// Helper to format exam date
const formatExamDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Helper to format percentage with 2 decimal places
const formatPercent = (value) => {
  if (value === null || value === undefined) return '0.00';
  return Number(value).toFixed(2);
};

// Helper to sort exams by date descending
const sortExamsByDate = (exams) => {
  return [...exams].sort((a, b) => {
    const dateA = new Date(a.created_at || 0);
    const dateB = new Date(b.created_at || 0);
    return dateB - dateA;
  });
};

// Helper to get exam scope description with short/full versions for tooltip display
const getExamScopeData = (exam) => {
  // Check cadernos first
  if (exam.cadernos && Array.isArray(exam.cadernos) && exam.cadernos.length > 0) {
    const cadernoNames = exam.cadernos.map(c => {
      if (c === 'caderno_1') return 'Caderno I';
      if (c === 'caderno_2') return 'Caderno II';
      if (c === 'caderno_com') return 'Comunicações';
      return c;
    });
    const fullText = cadernoNames.join(', ');
    return {
      short: cadernoNames.length > 1 ? `${cadernoNames.length} cadernos` : fullText,
      full: fullText,
      type: 'caderno'
    };
  }
  // Check sections - use exact names from database
  if (exam.sections && Array.isArray(exam.sections) && exam.sections.length > 0) {
    const sectionNames = exam.sections.map(s => {
      const nameMap = {
        'sec_aeronaves': 'Conhecimento Geral de Aeronaves',
        'sec_comunicacoes_en': 'Comunicações (Inglês)',
        'sec_comunicacoes_pt': 'Comunicações (Português)',
        'sec_humanas': 'Comportamento e Limitações Humanas',
        'sec_legislacao': 'Legislação Aérea',
        'sec_massa': 'Massa e Centragem',
        'sec_meteorologia': 'Meteorologia',
        'sec_navegacao': 'Navegação e Planeamento',
        'sec_operacionais': 'Procedimentos Operacionais',
        'sec_principios': 'Princípios de Voo'
      };
      return nameMap[s] || s;
    });
    const fullText = sectionNames.join(', ');
    return {
      short: sectionNames.length > 1 ? `${sectionNames.length} disciplinas` : fullText,
      full: fullText,
      type: 'section'
    };
  }
  return {
    short: 'Todas',
    full: 'Todas as disciplinas',
    type: 'all'
  };
};

// Simple helper for backward compatibility
const getExamScope = (exam) => getExamScopeData(exam).full;

// Component for displaying exam scope with tooltip
const ExamScopeBadge = ({ exam, className = "" }) => {
  const scopeData = getExamScopeData(exam);
  const colorClass = scopeData.type === 'caderno' 
    ? 'bg-orange-500/10 text-orange-400' 
    : scopeData.type === 'section'
      ? 'bg-emerald-500/10 text-emerald-400'
      : 'bg-slate-500/10 text-slate-400';
  
  return (
    <span 
      className={`text-xs px-2 py-0.5 rounded cursor-help ${colorClass} ${className}`}
      title={scopeData.full}
    >
      {scopeData.short}
    </span>
  );
};

const StudentDashboard = () => {
  const { user, logout, checkAuth, school } = useAuth();
  const navigate = useNavigate();
  // Reservas de voo: só aparece para quem tem ficha de piloto ligada à conta
  const [temFichaPiloto, setTemFichaPiloto] = useState(false);
  useEffect(() => {
    axios.get(`${API}/di/reservations/mine`, { withCredentials: true })
      .then(() => setTemFichaPiloto(true))
      .catch(() => setTemFichaPiloto(false));
  }, []);
  const [progress, setProgress] = useState([]);
  const [stats, setStats] = useState(null);
  const [badges, setBadges] = useState([]);
  const [missionStats, setMissionStats] = useState(null);
  const [activeMissions, setActiveMissions] = useState([]);
  const [leaderboardWidget, setLeaderboardWidget] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Subscription state
  const [subscription, setSubscription] = useState(null);
  
  // Accordion state for exam groups (both closed by default)
  const [evalGroupOpen, setEvalGroupOpen] = useState(false);
  const [prepGroupOpen, setPrepGroupOpen] = useState(false);
  
  // Edit name dialog state
  const [showEditNameDialog, setShowEditNameDialog] = useState(false);
  const [editingName, setEditingName] = useState("");
  const [savingName, setSavingName] = useState(false);
  
  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [firstBadge, setFirstBadge] = useState(null);
  
  // School request flow (Phase 4 multi-tenancy)
  const [showSchoolPicker, setShowSchoolPicker] = useState(false);
  const [requestedSchoolName, setRequestedSchoolName] = useState(null);
  const needsSchool = !user?.school_id && !user?.requested_school_id;
  const isPendingSchool = !user?.school_id && !!user?.requested_school_id;
  
  // Resolve the requested school name from the public list (best-effort)
  useEffect(() => {
    if (!isPendingSchool || !user?.requested_school_id) {
      setRequestedSchoolName(null);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/schools/public`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        const found = (r.data || []).find((s) => s.school_id === user.requested_school_id);
        setRequestedSchoolName(found?.name || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isPendingSchool, user?.requested_school_id]);
  
  const cancelSchoolRequest = async () => {
    try {
      await axios.delete(`${API}/me/request-school`, { withCredentials: true });
      toast.success("Pedido cancelado");
      await checkAuth();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao cancelar pedido");
    }
  };
  
  // Real-time exam polling (every 3 seconds)
  const { exams, lastUpdate } = useExamPolling(3000, true);
  
  // Track previous exam states to detect changes
  const prevExamsRef = useRef({});
  // Badge popup queue
  const [badgeQueue, setBadgeQueue] = useState([]);
  const [currentBadgePopup, setCurrentBadgePopup] = useState(null);
  const [showBadgePopup, setShowBadgePopup] = useState(false);
  
  // Handle name edit
  const handleEditName = () => {
    setEditingName(user?.name || "");
    setShowEditNameDialog(true);
  };
  
  const handleSaveName = async () => {
    if (!editingName.trim() || editingName.trim().length < 2) {
      toast.error("O nome deve ter pelo menos 2 caracteres");
      return;
    }
    
    setSavingName(true);
    try {
      await axios.put(`${API}/profile/name`, { name: editingName.trim() }, { withCredentials: true });
      toast.success("Nome atualizado com sucesso");
      setShowEditNameDialog(false);
      // Refresh user data
      if (checkAuth) {
        await checkAuth();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar nome");
    } finally {
      setSavingName(false);
    }
  };

  useEffect(() => {
    checkOnboarding();
    fetchDashboardData();
    fetchSubscriptionStatus();
  }, []);
  
  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setSubscription(response.data);
    } catch (error) {
      console.error("Error fetching subscription:", error);
    }
  };
  
  // Check for unseen badges after dashboard loads
  useEffect(() => {
    if (!loading && !showOnboarding) {
      checkUnseenBadges();
    }
  }, [loading, showOnboarding]);
  
  const checkUnseenBadges = async () => {
    try {
      const response = await axios.get(`${API}/badges/unseen`, { withCredentials: true });
      if (response.data && response.data.length > 0) {
        setBadgeQueue(response.data);
        setCurrentBadgePopup(response.data[0]);
        setShowBadgePopup(true);
      }
    } catch (error) {
      console.error("Error checking unseen badges:", error);
    }
  };
  
  const markBadgeSeen = async (badgeId) => {
    try {
      await axios.post(`${API}/badges/${badgeId}/mark-seen`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Error marking badge seen:", error);
    }
  };
  
  const showNextBadge = async () => {
    // Mark current badge as seen
    if (currentBadgePopup) {
      await markBadgeSeen(currentBadgePopup.badge_id);
    }
    
    const currentIndex = badgeQueue.findIndex(b => b.badge_id === currentBadgePopup?.badge_id);
    
    if (currentIndex < badgeQueue.length - 1) {
      // Show next badge
      setCurrentBadgePopup(badgeQueue[currentIndex + 1]);
    } else {
      // No more badges, close popup
      setShowBadgePopup(false);
      setCurrentBadgePopup(null);
      setBadgeQueue([]);
      // Refresh badges list
      fetchDashboardData();
    }
  };
  
  const checkOnboarding = async () => {
    try {
      const response = await axios.get(`${API}/auth/onboarding-status`, { withCredentials: true });
      if (response.data.needs_onboarding) {
        setFirstBadge(response.data.first_badge);
        setShowOnboarding(true);
      }
    } catch (error) {
      console.error("Error checking onboarding:", error);
    }
  };
  
  const completeOnboarding = async () => {
    try {
      await axios.post(`${API}/auth/complete-onboarding`, {}, { withCredentials: true });
      setShowOnboarding(false);
      // After onboarding, check for unseen badges
      setTimeout(() => checkUnseenBadges(), 500);
    } catch (error) {
      console.error("Error completing onboarding:", error);
      setShowOnboarding(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      // Note: exams are now fetched via useExamPolling hook for real-time updates
      const [progressRes, statsRes, badgesRes, missionStatsRes, missionsRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/progress`, { withCredentials: true }),
        axios.get(`${API}/stats`, { withCredentials: true }),
        axios.get(`${API}/badges/earned`, { withCredentials: true }),
        axios.get(`${API}/missions/stats`, { withCredentials: true }),
        axios.get(`${API}/missions`, { withCredentials: true }),
        axios.get(`${API}/leaderboard/widget`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      
      setProgress(progressRes.data);
      setStats(statsRes.data);
      setBadges(badgesRes.data);
      setMissionStats(missionStatsRes.data);
      setLeaderboardWidget(leaderboardRes.data);
      
      // Get active (non-completed) missions
      const allMissions = [
        ...missionsRes.data.daily,
        ...missionsRes.data.weekly
      ].filter(m => !m.progress?.completed).slice(0, 3);
      setActiveMissions(allMissions);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const startTraining = () => {
    navigate("/training");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cockpit-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cockpit-bg">
      {/* Header */}
      <header className="border-b border-cockpit-border bg-cockpit-panel/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-3 sm:px-6 py-2 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              {school?.logo_url ? (
                <img
                  src={school.logo_url}
                  alt={school.name}
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg object-contain border border-slate-700 bg-slate-900 p-0.5"
                  data-testid="school-logo"
                />
              ) : (
                <div
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center transition-colors"
                  style={{ background: school?.primary_color || "#f97316" }}
                >
                  <Plane className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              )}
              <div className="min-w-0">
                <h1 className="font-chivo text-sm sm:text-lg text-cockpit-text-bright whitespace-nowrap truncate max-w-[11rem] sm:max-w-sm" data-testid="header-school-name">
                  {school?.name || "ULM Training Centre"}
                </h1>
                <p className="text-[10px] sm:text-xs text-slate-500 font-mono hidden sm:block whitespace-nowrap">Painel do Aluno</p>
              </div>
            </div>
            
            <div className="flex items-center gap-1 sm:gap-2">
              {temFichaPiloto && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/reservas")}
                  className="text-slate-400 hover:text-orange-300 text-xs p-1.5 sm:p-2"
                  title="Reservar voo"
                  data-testid="dashboard-reservas"
                >
                  <CalendarCheck className="w-4 h-4" />
                  <span className="sr-only">Reservar voo</span>
                </Button>
              )}
              {temFichaPiloto && <SinoPessoal />}
              {temFichaPiloto && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/registar-voo")}
                  className="text-slate-400 hover:text-sky-300 text-xs p-1.5 sm:p-2"
                  title="Registar voo"
                  data-testid="dashboard-registar-voo"
                >
                  <PlaneTakeoff className="w-4 h-4" />
                  <span className="sr-only">Registar voo</span>
                </Button>
              )}
              {/* Navigation buttons */}
              {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'instructor') && (
                <Button 
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/instructor")}
                  className="text-slate-400 hover:text-cyan-400 text-xs p-1.5 sm:p-2"
                  title="Dashboard Instrutor"
                >
                  <GraduationCap className="w-4 h-4" />
                  <span className="sr-only">Instrutor</span>
                </Button>
              )}
              {(user?.role === 'admin' || user?.role === 'super_admin') && (
                <Button 
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/admin")}
                  className="text-slate-400 hover:text-red-400 text-xs p-1.5 sm:p-2"
                  title="Painel Admin"
                >
                  <Shield className="w-4 h-4" />
                  <span className="sr-only">Admin</span>
                </Button>
              )}
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => navigate("/leaderboard")}
                className="text-slate-400 hover:text-yellow-400 text-xs p-1.5 sm:p-2 hidden sm:flex"
                title="Leaderboard"
              >
                <Trophy className="w-4 h-4" />
                <span className="sr-only">Ranking</span>
              </Button>
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => navigate("/missions")}
                className="text-slate-400 hover:text-purple-400 text-xs p-1.5 sm:p-2 hidden sm:flex"
                title="Missões"
              >
                <Rocket className="w-4 h-4" />
                <span className="sr-only">Missões</span>
              </Button>
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => navigate("/badges")}
                className="text-slate-400 hover:text-yellow-400 text-xs p-1.5 sm:p-2 hidden sm:flex"
                title="Badges"
              >
                <Award className="w-4 h-4" />
                <span className="sr-only">Badges</span>
              </Button>
              <PushNotificationToggle />
              <div className="w-px h-4 sm:h-6 bg-slate-700 mx-1 sm:mx-2" />
              
              {/* User section - Avatar dropdown on mobile, full info on desktop */}
              <div className="flex items-center gap-2 sm:gap-3">
                {/* Desktop: show name and email */}
                <div className="text-right hidden sm:block">
                  <p className="text-sm text-cockpit-text-bright">{user?.name}</p>
                  <p className="text-xs text-slate-500 font-mono">{user?.email}</p>
                </div>
                
                {/* Avatar with dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="w-8 h-8 sm:w-9 sm:h-9 rounded-full overflow-hidden border-2 border-slate-600 hover:border-orange-500 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500/50">
                      {user?.picture ? (
                        <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-orange-500 flex items-center justify-center text-white font-bold text-sm">
                          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700 min-w-[220px]">
                    <div className="px-3 py-2 border-b border-slate-700">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-cockpit-text-bright font-medium">{user?.name}</p>
                        <button 
                          onClick={(e) => { e.preventDefault(); handleEditName(); }}
                          className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white transition-colors"
                          title="Editar nome"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <p className="text-xs text-slate-500">{user?.email}</p>
                      {/* Subscription Status */}
                      {subscription && (
                        <div className="mt-2 space-y-1">
                          <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${
                            subscription.plan_type === "admin_granted" 
                              ? "bg-purple-500/20 text-purple-400" 
                              : subscription.plan_type === "premium"
                                ? "bg-yellow-500/20 text-yellow-400"
                                : subscription.plan_type === "trial"
                                  ? "bg-blue-500/20 text-blue-400"
                                  : "bg-red-500/20 text-red-400"
                          }`}>
                            {subscription.plan_type === "admin_granted" && "👑 Premium Ilimitado"}
                            {subscription.plan_type === "premium" && `⭐ Premium`}
                            {subscription.plan_type === "trial" && `🔵 Trial (${subscription.days_remaining}d)`}
                            {subscription.plan_type === "expired" && "🔴 Expirado"}
                          </span>
                          {subscription.plan_type === "premium" && subscription.subscription_ends_at && (
                            <p className="text-xs text-slate-400">
                              {subscription.has_stripe_subscription 
                                ? `🔄 Renova: ${new Date(subscription.subscription_ends_at).toLocaleDateString('pt-PT')}`
                                : `📅 Expira: ${new Date(subscription.subscription_ends_at).toLocaleDateString('pt-PT')}`}
                            </p>
                          )}
                          {subscription.plan_type === "trial" && subscription.trial_ends_at && (
                            <p className="text-xs text-slate-400">
                              📅 Trial até: {new Date(subscription.trial_ends_at).toLocaleDateString('pt-PT')}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Mobile navigation items */}
                    <div className="sm:hidden py-1">
                      <DropdownMenuItem onClick={() => navigate("/leaderboard")} className="text-slate-300 hover:text-white cursor-pointer">
                        <Trophy className="w-4 h-4 mr-2 text-yellow-400" />
                        Ranking
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/missions")} className="text-slate-300 hover:text-white cursor-pointer">
                        <Rocket className="w-4 h-4 mr-2 text-purple-400" />
                        Missões
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/badges")} className="text-slate-300 hover:text-white cursor-pointer">
                        <Award className="w-4 h-4 mr-2 text-yellow-400" />
                        Badges
                      </DropdownMenuItem>
                    </div>
                    {/* Subscription management link */}
                    <DropdownMenuItem onClick={() => navigate("/subscription")} className="text-slate-300 hover:text-white cursor-pointer">
                      <Crown className="w-4 h-4 mr-2 text-yellow-400" />
                      Gerir Subscrição
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(helpPath("aluno"))} className="text-slate-300 hover:text-white cursor-pointer" data-testid="menu-help">
                      <HelpBook className="w-4 h-4 mr-2 text-sky-400" />
                      Manual de ajuda
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleLogout} className="text-red-400 hover:text-red-300 cursor-pointer">
                      <LogOut className="w-4 h-4 mr-2" />
                      Terminar Sessão
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </div>
      </header>
      {/* Main Content */}
      <main className="container mx-auto px-2 sm:px-6 py-4 sm:py-8">
        {/* School Request Banner — Phase 4 multi-tenancy */}
        {needsSchool && (
          <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-center justify-between gap-3" data-testid="banner-no-school">
            <div className="flex items-center gap-3">
              <School className="w-5 h-5 text-amber-400 shrink-0" />
              <div>
                <p className="text-amber-300 font-medium">Ainda não estás associado a nenhuma escola</p>
                <p className="text-sm text-slate-400">Seleciona a tua escola para que um administrador te associe a uma turma.</p>
              </div>
            </div>
            <Button
              onClick={() => setShowSchoolPicker(true)}
              className="btn-cockpit shrink-0"
              data-testid="open-school-picker"
            >
              <School className="w-4 h-4 mr-2" />
              Selecionar Escola
            </Button>
          </div>
        )}
        {isPendingSchool && (
          <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-center justify-between gap-3" data-testid="banner-pending-school">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-blue-400 shrink-0" />
              <div>
                <p className="text-blue-300 font-medium">
                  Pedido enviado{requestedSchoolName ? ` para ${requestedSchoolName}` : ""}
                </p>
                <p className="text-sm text-slate-400">A aguardar aprovação do administrador da escola. Podes continuar a treinar no entretanto.</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={cancelSchoolRequest}
              className="btn-secondary-cockpit shrink-0"
              data-testid="cancel-school-request"
            >
              Cancelar pedido
            </Button>
          </div>
        )}

        {/* Subscription Banner - Trial Expired */}
        {subscription && !subscription.can_access_full && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <div>
                <p className="text-red-400 font-medium">O seu período de teste terminou</p>
                <p className="text-sm text-slate-400">Subscreva para continuar a usar todas as funcionalidades.</p>
              </div>
            </div>
            <Button 
              onClick={() => navigate("/subscription")}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              <Crown className="w-4 h-4 mr-2" />
              Subscrever
            </Button>
          </div>
        )}
        
        {/* Trial Banner - Only for trial users (not paid premium) */}
        {subscription && subscription.plan_type === "trial" && subscription.can_access_full && (
          <div className={`mb-6 p-4 rounded-lg flex items-center justify-between ${
            subscription.days_remaining <= 3 
              ? "bg-yellow-500/10 border border-yellow-500/30" 
              : subscription.days_remaining <= 5
                ? "bg-orange-500/10 border border-orange-500/30"
                : "bg-blue-500/10 border border-blue-500/30"
          }`}>
            <div className="flex items-center gap-3">
              <Clock className={`w-5 h-5 ${
                subscription.days_remaining <= 3 
                  ? "text-yellow-400" 
                  : subscription.days_remaining <= 5
                    ? "text-orange-400"
                    : "text-blue-400"
              }`} />
              <div>
                <p className={`font-medium ${
                  subscription.days_remaining <= 3 
                    ? "text-yellow-400" 
                    : subscription.days_remaining <= 5
                      ? "text-orange-400"
                      : "text-blue-400"
                }`}>
                  {subscription.days_remaining <= 1 
                    ? "⚠️ Último dia de teste grátis!" 
                    : `Teste grátis: ${subscription.days_remaining} dias restantes`}
                </p>
                <p className="text-sm text-slate-400">
                  {subscription.days_remaining <= 3 
                    ? "Subscreva agora para não perder acesso às disciplinas." 
                    : "Aproveite o acesso completo. Subscreva para continuar após o período de teste."}
                </p>
              </div>
            </div>
            <Button 
              onClick={() => navigate("/subscription")}
              className={`${
                subscription.days_remaining <= 3 
                  ? "bg-yellow-500 hover:bg-yellow-600 text-black" 
                  : subscription.days_remaining <= 5
                    ? "bg-orange-500 hover:bg-orange-600 text-white"
                    : "bg-blue-500 hover:bg-blue-600 text-white"
              }`}
            >
              <Crown className="w-4 h-4 mr-2" />
              {subscription.days_remaining <= 3 ? "Subscrever Agora" : "Ver Planos"}
            </Button>
          </div>
        )}
        
        {/* Premium Renewal Banner - For paid users with subscription ending soon */}
        {subscription && subscription.plan_type === "premium" && subscription.can_access_full && subscription.days_remaining <= 7 && (
          <div className={`mb-6 p-4 rounded-lg flex items-center justify-between ${
            subscription.days_remaining <= 3 
              ? "bg-yellow-500/10 border border-yellow-500/30" 
              : "bg-emerald-500/10 border border-emerald-500/30"
          }`}>
            <div className="flex items-center gap-3">
              <Crown className={`w-5 h-5 ${
                subscription.days_remaining <= 3 ? "text-yellow-400" : "text-emerald-400"
              }`} />
              <div>
                <p className={`font-medium ${
                  subscription.days_remaining <= 3 ? "text-yellow-400" : "text-emerald-400"
                }`}>
                  {subscription.days_remaining <= 1 
                    ? "⚠️ Subscrição expira amanhã!" 
                    : subscription.days_remaining <= 3
                      ? `⚠️ Subscrição expira em ${subscription.days_remaining} dias`
                      : `Subscrição Premium: ${subscription.days_remaining} dias restantes`}
                </p>
                <p className="text-sm text-slate-400">
                  {subscription.has_stripe_subscription 
                    ? "A sua subscrição será renovada automaticamente." 
                    : subscription.days_remaining <= 3
                      ? "Renove a sua subscrição para manter o acesso completo."
                      : "Obrigado por ser membro Premium!"}
                </p>
              </div>
            </div>
            <Button 
              onClick={() => navigate("/subscription")}
              variant={subscription.days_remaining <= 3 ? "default" : "outline"}
              className={subscription.days_remaining <= 3 
                ? "bg-yellow-500 hover:bg-yellow-600 text-black" 
                : "border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"}
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Gerir Subscrição
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 sm:gap-6">
          {/* Stats Cards */}
          <div className="lg:col-span-4 grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard 
              icon={<Flame className="w-5 h-5" />}
              value={stats?.current_streak || 0}
              label="Dias Seguidos"
              color="orange"
            />
            <StatCard 
              icon={<Target className="w-5 h-5" />}
              value={stats?.total_correct || 0}
              label="Respostas Corretas"
              color="emerald"
            />
            <StatCard 
              icon={<BookOpen className="w-5 h-5" />}
              value={stats?.total_sessions || 0}
              label="Sessões"
              color="cyan"
            />
            <StatCard 
              icon={<Trophy className="w-5 h-5" />}
              value={stats?.badges_earned || 0}
              label="Badges"
              color="yellow"
              onClick={() => navigate('/badges')}
            />
            <StatCard 
              icon={<Zap className="w-5 h-5" />}
              value={missionStats?.total_xp || 0}
              label={missionStats?.rank?.name || "Cadete"}
              color="purple"
              onClick={() => navigate('/missions')}
            />
          </div>

          {/* Main Action - Start Training */}
          <div className="lg:col-span-2">
            <Card className="instrument-panel h-full">
              <CardContent className="p-8 flex flex-col items-center justify-center text-center h-full">
                <div className="w-20 h-20 bg-orange-500/20 rounded-full flex items-center justify-center mb-6">
                  <Play className="w-10 h-10 text-orange-500" />
                </div>
                <h2 className="font-chivo text-2xl text-cockpit-text-bright mb-3">Iniciar Treino</h2>
                <p className="text-slate-400 mb-6 max-w-sm">
                  Sessão adaptativa baseada no seu desempenho. Escolha a disciplina, caderno e número de questões.
                </p>
                <Button 
                  onClick={startTraining}
                  data-testid="start-training-btn"
                  className="btn-cockpit text-base px-8 py-4"
                >
                  Treinar Agora
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Exams Section - Only show if user has a class assigned */}
          {user?.class_id ? (
          <div className="lg:col-span-2">
            <Card className="instrument-panel h-full">
              <CardHeader className="pb-4">
                <CardTitle className="font-chivo text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-cyan-400" />
                  Exames
                  {/* Real-time indicator */}
                  <span className="ml-auto flex items-center gap-1.5 text-xs font-normal">
                    <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                    <span className="text-slate-500">Tempo real</span>
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {(() => {
                  // Separate exams by type
                  const evaluationExams = exams.filter(e => e.counts_for_stats !== false);
                  const preparationExams = exams.filter(e => e.counts_for_stats === false);

                  // Helper to render exam sub-sections for a given list of exams
                  const renderExamList = (examList) => (
                    <div className="space-y-3 pt-1">
                      {/* Draft/Pending */}
                      {examList.filter(e => e.status === 'draft' && !e.completed).length > 0 && (
                        <div>
                          <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-2">
                            <Lock className="w-3 h-3" />
                            Agendados (aguardam início)
                          </h4>
                          <div className="space-y-2">
                            {examList.filter(e => e.status === 'draft' && !e.completed).map((exam) => (
                              <div key={exam.exam_id} className="p-3 bg-slate-800/50 rounded-lg border border-slate-700 opacity-75">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="text-cockpit-text-bright font-medium text-sm">{exam.title}</h4>
                                      {exam.created_at && (
                                        <span className="text-xs text-slate-500">{formatExamDate(exam.created_at)}</span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 mt-1">
                                      <ExamScopeBadge exam={exam} />
                                      <span className="text-xs text-slate-500 font-mono">
                                        {exam.num_questions || '?'} questões · {exam.duration_minutes} min
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="badge-cockpit text-slate-400 text-xs">Aguarda início</span>
                                    <Lock className="w-4 h-4 text-slate-500" />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Active Exams */}
                      {examList.filter(e => (e.status === 'active' || !e.status) && !e.completed && !e.in_progress).length > 0 && (
                        <div>
                          <h4 className="text-xs uppercase tracking-wider text-emerald-400 mb-2 flex items-center gap-2">
                            <Bell className="w-3 h-3 animate-bounce" />
                            Disponíveis AGORA
                          </h4>
                          <div className="space-y-2">
                            {examList.filter(e => (e.status === 'active' || !e.status) && !e.completed && !e.in_progress).map((exam) => {
                              const timeMin = exam.time_remaining_seconds !== null ? Math.floor(exam.time_remaining_seconds / 60) : null;
                              const timeSec = exam.time_remaining_seconds !== null ? exam.time_remaining_seconds % 60 : null;
                              return (
                                <div
                                  key={exam.exam_id}
                                  className="p-4 bg-emerald-500/20 rounded-lg border-2 border-emerald-500/50 hover:border-emerald-400 transition-all cursor-pointer group animate-pulse-slow"
                                  onClick={() => navigate(`/exam/${exam.exam_id}`)}
                                  data-testid={`exam-${exam.exam_id}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <h4 className="text-cockpit-text-bright font-medium text-base group-hover:text-emerald-400 transition-colors">{exam.title}</h4>
                                        <span className="text-xs text-slate-500">{formatExamDate(exam.created_at)}</span>
                                      </div>
                                      <div className="flex items-center gap-2 mt-1">
                                        <ExamScopeBadge exam={exam} />
                                        <span className="text-xs text-slate-400 font-mono">
                                          {exam.num_questions || '?'} questões · {exam.duration_minutes} min
                                        </span>
                                      </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                      {timeMin !== null && (
                                        <span className="text-lg font-mono text-orange-400 font-bold">
                                          {timeMin}:{timeSec?.toString().padStart(2, '0')}
                                        </span>
                                      )}
                                      <span className="px-3 py-1 bg-emerald-500 text-white text-xs font-bold rounded animate-pulse">ENTRAR</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* In Progress */}
                      {examList.filter(e => e.in_progress && !e.completed && !e.awaiting_submission).length > 0 && (
                        <div>
                          <h4 className="text-xs uppercase tracking-wider text-yellow-400 mb-2">Em Curso</h4>
                          <div className="space-y-2">
                            {examList.filter(e => e.in_progress && !e.completed && !e.awaiting_submission).map((exam) => (
                              <div
                                key={exam.exam_id}
                                className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30 hover:border-yellow-500/50 transition-colors cursor-pointer group"
                                onClick={() => navigate(`/exam/${exam.exam_id}`)}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="text-cockpit-text-bright font-medium text-sm">{exam.title}</h4>
                                      <span className="text-xs text-slate-500">{formatExamDate(exam.created_at)}</span>
                                    </div>
                                    <div className="flex items-center gap-2 mt-1">
                                      <ExamScopeBadge exam={exam} />
                                      <span className="text-xs text-yellow-400 font-mono">Continuar exame</span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="badge-cockpit text-yellow-400 text-xs">Em curso</span>
                                    <ChevronRight className="w-4 h-4 text-yellow-400" />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Awaiting Correction */}
                      {examList.filter(e => e.awaiting_submission && !e.completed).length > 0 && (
                        <div>
                          <h4 className="text-xs uppercase tracking-wider text-orange-400 mb-2">A Aguardar Correção</h4>
                          <div className="space-y-2">
                            {examList.filter(e => e.awaiting_submission && !e.completed).map((exam) => (
                              <div key={exam.exam_id} className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/30">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="text-cockpit-text-bright font-medium text-sm">{exam.title}</h4>
                                      <span className="text-xs text-slate-500">{formatExamDate(exam.created_at)}</span>
                                    </div>
                                    <div className="flex items-center gap-2 mt-1">
                                      <ExamScopeBadge exam={exam} />
                                      <span className="text-xs text-orange-400">A aguardar correção</span>
                                    </div>
                                  </div>
                                  <Clock className="w-4 h-4 text-orange-400" />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Completed */}
                      {sortExamsByDate(examList.filter(e => e.completed)).length > 0 && (
                        <div>
                          <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Concluídos</h4>
                          <div className="space-y-2">
                            {sortExamsByDate(examList.filter(e => e.completed)).map((exam) => {
                              const isUnlocked = exam.result?.review_unlocked;
                              return (
                                <div
                                  key={exam.exam_id}
                                  className={`p-3 rounded-lg border transition-colors ${
                                    isUnlocked
                                      ? 'cursor-pointer group ' + (exam.result?.passed
                                          ? 'bg-emerald-500/10 border-emerald-500/30 hover:border-emerald-500/50'
                                          : 'bg-red-500/10 border-red-500/30 hover:border-red-500/50')
                                      : 'bg-orange-500/10 border-orange-500/30'
                                  }`}
                                  onClick={() => isUnlocked && navigate(`/exam/${exam.exam_id}/review`)}
                                >
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <h4 className="text-cockpit-text-bright font-medium text-sm">{exam.title}</h4>
                                        <span className="text-xs text-slate-500">{formatExamDate(exam.created_at)}</span>
                                      </div>
                                      <div className="flex items-center gap-2 mt-1">
                                        <ExamScopeBadge exam={exam} />
                                        {isUnlocked ? (
                                          <span className={`text-xs font-mono ${exam.result?.passed ? 'text-emerald-400' : 'text-red-400'}`}>
                                            {formatPercent(exam.result?.accuracy)}% · {exam.result?.passed ? 'Aprovado' : 'Reprovado'}
                                          </span>
                                        ) : (
                                          <span className="text-xs text-orange-400">A aguardar correção</span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {isUnlocked ? (
                                        <>
                                          <span className={`badge-cockpit text-xs ${exam.result?.passed ? 'text-emerald-400' : 'text-red-400'}`}>
                                            {exam.result?.passed ? '✓' : '✗'}
                                          </span>
                                          <Eye className="w-4 h-4 text-slate-500 group-hover:text-white" />
                                        </>
                                      ) : (
                                        <Clock className="w-4 h-4 text-orange-400" />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );

                  return (
                    <>
                      {/* Group: Exames de Avaliação */}
                      {evaluationExams.length > 0 && (
                        <div className="rounded-lg border border-slate-700 overflow-hidden">
                          <button
                            className="w-full flex items-center justify-between px-4 py-3 bg-slate-800/60 hover:bg-slate-800 transition-colors text-left"
                            onClick={() => setEvalGroupOpen(v => !v)}
                            data-testid="exam-group-evaluation-toggle"
                          >
                            <div className="flex items-center gap-2">
                              <BookMarked className="w-4 h-4 text-cyan-400" />
                              <span className="text-sm font-medium text-cockpit-text-bright">Exames</span>
                              <span className="text-xs text-slate-500 font-mono bg-slate-700 px-1.5 py-0.5 rounded">
                                {evaluationExams.length}
                              </span>
                            </div>
                            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${evalGroupOpen ? 'rotate-180' : ''}`} />
                          </button>
                          {evalGroupOpen && (
                            <div className="px-4 pb-3">
                              {renderExamList(evaluationExams)}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Group: Exames de Preparação */}
                      {preparationExams.length > 0 && (
                        <div className="rounded-lg border border-slate-700 overflow-hidden">
                          <button
                            className="w-full flex items-center justify-between px-4 py-3 bg-slate-800/60 hover:bg-slate-800 transition-colors text-left"
                            onClick={() => setPrepGroupOpen(v => !v)}
                            data-testid="exam-group-preparation-toggle"
                          >
                            <div className="flex items-center gap-2">
                              <FlaskConical className="w-4 h-4 text-purple-400" />
                              <span className="text-sm font-medium text-cockpit-text-bright">Exames de Preparação</span>
                              <span className="text-xs text-slate-500 font-mono bg-slate-700 px-1.5 py-0.5 rounded">
                                {preparationExams.length}
                              </span>
                            </div>
                            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${prepGroupOpen ? 'rotate-180' : ''}`} />
                          </button>
                          {prepGroupOpen && (
                            <div className="px-4 pb-3">
                              {renderExamList(preparationExams)}
                            </div>
                          )}
                        </div>
                      )}

                      {/* No Exams */}
                      {exams.length === 0 && (
                        <div className="text-center py-8 text-slate-500">
                          <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">Nenhum exame disponível</p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
          ) : (
          /* No class assigned - show message instead of exams */
          <div className="lg:col-span-2">
            <Card className="instrument-panel h-full">
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-slate-500 mx-auto mb-4 opacity-50" />
                <h3 className="font-chivo text-lg text-cockpit-text-bright mb-2">
                  Sem Turma Atribuída
                </h3>
                <p className="text-sm text-slate-400">
                  Área destinada a exames de alunos PU. Contacte o seu instrutor para ser adicionado a uma turma e ter acesso aos exames caso seja essa a sua condição.
                </p>
              </CardContent>
            </Card>
          </div>
          )}

          {/* Skill Rating History — full-width compact row */}
          <div className="lg:col-span-4">
            <SkillRatingHistory />
          </div>

          {/* Flight Planning Panel — webcam, weather, NOTAMs */}
          <div className="lg:col-span-4" data-testid="student-flight-planning-section">
            <FlightPlanningPanel />
          </div>

          {/* Teóricas, instrução prática, documentos e voos reservados: abaixo do METAR e antes do progresso */}
          <div className="lg:col-span-4 flex flex-col gap-3 sm:gap-4" data-testid="student-blocos-pilotos-teoricas">
            {user?.class_id && <AnacStudentPanel />}
            {temFichaPiloto && <InstrucaoPratica />}
            {temFichaPiloto && <DocumentosValidades />}
            {temFichaPiloto && <ProximosVoos />}
          </div>

          {/* Progress by Section - Organized by Caderno */}
          <div className="lg:col-span-3"> 
            <Card className="instrument-panel">
              <CardHeader className="pb-3 sm:pb-4 px-2 sm:px-6">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-chivo text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-400" />
                    Progresso por Disciplina
                  </CardTitle>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  O domínio aumenta com respostas corretas e diminui com erradas.
                  Clique para treinar a disciplina.
                </p>
              </CardHeader>
              <CardContent className="space-y-2 sm:space-y-4 px-2 sm:px-6">
                {progress.length > 0 ? (
                  // Sort by caderno_id then by section_name
                  [...progress]
                    .sort((a, b) => {
                      // First sort by caderno
                      const cadernoOrder = { 'caderno_1': 1, 'caderno_2': 2, 'caderno_com': 3 };
                      const cadernoA = cadernoOrder[a.caderno_id] || 99;
                      const cadernoB = cadernoOrder[b.caderno_id] || 99;
                      if (cadernoA !== cadernoB) return cadernoA - cadernoB;
                      // Then sort by section name
                      return (a.section_name || '').localeCompare(b.section_name || '');
                    })
                    .map((section) => (
                      <ProgressItem 
                        key={section.section_id} 
                        section={section} 
                        onStartTraining={(sectionId, sectionName) => {
                          navigate('/training', { 
                            state: { 
                              preselectedSection: sectionId,
                              preselectedSectionName: sectionName 
                            } 
                          });
                        }}
                      />
                    ))
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Comece a treinar para ver o seu progresso</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent Badges */}
          <div className="lg:col-span-1">
            <Card 
              className="instrument-panel cursor-pointer hover:border-yellow-500/30 transition-colors"
              onClick={() => navigate('/badges')}
            >
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-chivo text-lg flex items-center gap-2">
                    <Award className="w-5 h-5 text-yellow-400" />
                    Badges
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {badges.length > 0 ? (
                  <div className="grid grid-cols-3 gap-3">
                    {badges.slice(0, 6).map((badge) => (
                      <BadgeIcon key={badge.badge_id} badge={badge} onNavigate={() => navigate('/badges')} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-500">
                    <Star className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-xs">Conquiste badges treinando</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Active Missions */}
            <Card 
              className="instrument-panel mt-6 cursor-pointer hover:border-purple-500/30 transition-colors"
              onClick={() => navigate('/missions')}
            >
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-chivo text-lg flex items-center gap-2">
                    <Rocket className="w-5 h-5 text-purple-400" />
                    Missões
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {activeMissions.length > 0 ? (
                  activeMissions.map((mission) => (
                    <div 
                      key={mission.mission_id}
                      className="p-3 bg-slate-900/50 rounded-lg border border-slate-800"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-cockpit-text-bright">{mission.name}</span>
                        <span className="text-xs font-mono text-orange-400">{mission.progress?.percentage || 0}%</span>
                      </div>
                      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-orange-500 transition-all"
                          style={{ width: `${mission.progress?.percentage || 0}%` }}
                        />
                      </div>
                      <div className="flex items-center gap-1 mt-2 text-xs text-yellow-400">
                        <Zap className="w-3 h-3" />
                        <span>{mission.xp_reward} XP</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 text-slate-500">
                    <Rocket className="w-6 h-6 mx-auto mb-2 opacity-50" />
                    <p className="text-xs">Todas as missões completas!</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Leaderboard Widget */}
            <Card className="instrument-panel mt-6"
              onClick={() => navigate('/leaderboard')}
            >
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-chivo text-lg flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-yellow-400" />
                    Ranking
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {leaderboardWidget?.class_leaderboard?.top5?.length > 0 ? (
                  <div className="space-y-2">
                    {/* Top 5 da turma */}
                    {leaderboardWidget.class_leaderboard.top5.map((entry, idx) => (
                      <div 
                        key={entry.user_id}
                        className={`flex items-center gap-3 p-2 rounded-lg ${
                          entry.user_id === user?.user_id 
                            ? 'bg-orange-500/10 border border-orange-500/30' 
                            : 'bg-slate-900/50'
                        }`}
                      >
                        <span className="w-6 flex justify-center">
                          {idx === 0 && (
                            <div className="w-5 h-5 rounded-full bg-gradient-to-b from-yellow-400 to-amber-600 flex items-center justify-center">
                              <Trophy className="w-3 h-3 text-white" />
                            </div>
                          )}
                          {idx === 1 && (
                            <div className="w-5 h-5 rounded-full bg-gradient-to-b from-slate-300 to-slate-500 flex items-center justify-center">
                              <Star className="w-3 h-3 text-white" />
                            </div>
                          )}
                          {idx === 2 && (
                            <div className="w-5 h-5 rounded-full bg-gradient-to-b from-orange-400 to-orange-700 flex items-center justify-center">
                              <Plane className="w-3 h-3 text-white" />
                            </div>
                          )}
                          {idx > 2 && <span className="text-xs text-slate-500">#{idx + 1}</span>}
                        </span>
                        <span className={`flex-1 text-xs truncate ${
                          entry.user_id === user?.user_id ? 'text-orange-400' : 'text-cockpit-text-bright'
                        }`}>
                          {entry.name}
                        </span>
                        <span className="text-xs font-mono text-yellow-400">{entry.total_score}</span>
                      </div>
                    ))}
                    
                    {/* Posição do utilizador se fora do top 5 */}
                    {leaderboardWidget.class_leaderboard.user_position > 5 && leaderboardWidget.class_leaderboard.user_score && (
                      <>
                        <div className="flex items-center gap-2 my-1">
                          <div className="flex-1 h-px bg-slate-700"></div>
                          <span className="text-[10px] text-slate-600">...</span>
                          <div className="flex-1 h-px bg-slate-700"></div>
                        </div>
                        <div className="flex items-center gap-3 p-2 rounded-lg bg-orange-500/10 border border-orange-500/30">
                          <span className="w-6 text-center text-xs text-slate-500">
                            #{leaderboardWidget.class_leaderboard.user_position}
                          </span>
                          <span className="flex-1 text-xs truncate text-orange-400">
                            {leaderboardWidget.class_leaderboard.user_score.name}
                          </span>
                          <span className="text-xs font-mono text-yellow-400">
                            {leaderboardWidget.class_leaderboard.user_score.total_score}
                          </span>
                        </div>
                      </>
                    )}
                    
                    <p className="text-[10px] text-slate-600 text-center mt-2">
                      Ranking semanal da turma · {leaderboardWidget.class_leaderboard.total_in_class} alunos
                    </p>
                  </div>
                ) : leaderboardWidget?.global_position ? (
                  <div className="text-center py-4">
                    <div className="text-3xl font-mono text-orange-400 mb-1">
                      #{leaderboardWidget.global_position}
                    </div>
                    <p className="text-xs text-slate-500">
                      Posição global de {leaderboardWidget.global_total}
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-4 text-slate-500">
                    <Trophy className="w-6 h-6 mx-auto mb-2 opacity-50" />
                    <p className="text-xs">Treine para entrar no ranking!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Onboarding Dialog */}
        <Dialog open={showOnboarding} onOpenChange={(open) => { if (!open) return; }}>
          <DialogContent className="bg-cockpit-panel border-cockpit-border max-w-lg [&>button]:hidden">
            <DialogHeader>
              <DialogTitle className="font-chivo text-center text-xl">
                {onboardingStep === 0 && "Bem-vindo ao ULM Training Centre!"}
                {onboardingStep === 1 && "Sistema de Treino"}
                {onboardingStep === 2 && "Missões e Badges"}
                {onboardingStep === 3 && "Primeiro Badge!"}
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              {onboardingStep === 0 && (
                <div className="text-center space-y-4">
                  <Plane className="w-16 h-16 mx-auto text-orange-400" />
                  <p className="text-cockpit-text-bright">
                    O ULM Training Centre é a sua plataforma de treino para a licença de piloto ULM.
                  </p>
                  <p className="text-sm text-slate-400">
                    Aqui vai encontrar questões de exame, treinos adaptativos e um sistema de gamificação para o motivar.
                  </p>
                </div>
              )}
              
              {onboardingStep === 1 && (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg">
                    <BookOpen className="w-6 h-6 text-cyan-400 flex-shrink-0" />
                    <div>
                      <h4 className="text-cockpit-text-bright font-medium">Treino Adaptativo</h4>
                      <p className="text-xs text-slate-400">O sistema adapta as questões ao seu nível e foca nas áreas onde precisa de mais prática.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg">
                    <Target className="w-6 h-6 text-orange-400 flex-shrink-0" />
                    <div>
                      <h4 className="text-cockpit-text-bright font-medium">Exames Simulados</h4>
                      <p className="text-xs text-slate-400">Teste os seus conhecimentos com exames cronometrados que simulam as condições reais.</p>
                    </div>
                  </div>
                </div>
              )}
              
              {onboardingStep === 2 && (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg">
                    <Zap className="w-6 h-6 text-yellow-400 flex-shrink-0" />
                    <div>
                      <h4 className="text-cockpit-text-bright font-medium">Missões Diárias</h4>
                      <p className="text-xs text-slate-400">Complete missões para ganhar XP e subir de nível. Há missões diárias, semanais e especiais!</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg">
                    <Award className="w-6 h-6 text-purple-400 flex-shrink-0" />
                    <div>
                      <h4 className="text-cockpit-text-bright font-medium">Badges</h4>
                      <p className="text-xs text-slate-400">Desbloqueie badges especiais ao atingir marcos importantes na sua jornada de aprendizagem.</p>
                    </div>
                  </div>
                </div>
              )}
              
              {onboardingStep === 3 && (
                <div className="text-center space-y-4">
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-6 inline-block">
                    <Plane className="w-12 h-12 text-orange-400 mx-auto mb-3" />
                    <h3 className="text-cockpit-text-bright font-medium text-lg">
                      {firstBadge?.name || "Primeiro Voo"}
                    </h3>
                    <p className="text-xs text-slate-400 mt-1">
                      {firstBadge?.description || "Completou o registo na plataforma"}
                    </p>
                  </div>
                  <p className="text-sm text-cockpit-text-bright">
                    Parabéns! Acabou de receber o seu primeiro badge!
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex gap-3">
              {onboardingStep > 0 && (
                <Button
                  variant="outline"
                  onClick={() => setOnboardingStep(onboardingStep - 1)}
                  className="flex-1"
                >
                  Anterior
                </Button>
              )}
              {onboardingStep < 3 ? (
                <Button
                  onClick={() => setOnboardingStep(onboardingStep + 1)}
                  className="btn-cockpit flex-1"
                >
                  Seguinte
                </Button>
              ) : (
                <Button
                  onClick={completeOnboarding}
                  className="btn-cockpit flex-1"
                >
                  Começar a Treinar!
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Badge Achievement Popup - Styled like BadgesPage */}
        <Dialog open={showBadgePopup} onOpenChange={() => {}}>
          <DialogContent className="bg-cockpit-panel border-cockpit-border max-w-md [&>button]:hidden">
            <DialogHeader>
              <DialogTitle className="font-chivo text-center flex items-center justify-center gap-2">
                <Award className="w-6 h-6 text-orange-400" />
                Novo Badge Desbloqueado!
              </DialogTitle>
            </DialogHeader>
            <div className="py-6 text-center">
              {/* Badge Icon Circle - Same style as BadgesPage */}
              <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4 bg-gradient-to-b from-orange-500/30 to-orange-600/20 border-2 border-orange-500/50">
                {(() => {
                  const IconComponent = badgeIconMap[currentBadgePopup?.icon] || Award;
                  return <IconComponent className="w-12 h-12 text-orange-400" />;
                })()}
              </div>
              
              {/* Badge Name */}
              <h3 className="text-cockpit-text-bright font-chivo text-xl mb-2">
                {currentBadgePopup?.name || 'Badge'}
              </h3>
              
              {/* Badge Description */}
              {currentBadgePopup?.description && (
                <p className="text-sm text-slate-400 mb-4">{currentBadgePopup.description}</p>
              )}
              
              {/* Reason Box - "Why you earned this" */}
              <div className="bg-slate-900/50 rounded-lg p-4 mt-4 mb-4">
                <p className="text-xs text-slate-500 uppercase mb-1">Motivo da conquista</p>
                <p className="text-sm text-slate-300">
                  {badgeRequirementText[currentBadgePopup?.requirement] || currentBadgePopup?.description || 'Parabéns pela conquista!'}
                </p>
              </div>
              
              {/* Earned Date */}
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg mb-4">
                <p className="text-xs text-emerald-400">
                  ✓ Conquistado em {new Date().toLocaleDateString('pt-PT', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
              
              {/* Queue Counter */}
              {badgeQueue.length > 1 && (
                <div className="mb-4 text-xs text-slate-500">
                  {badgeQueue.findIndex(b => b.badge_id === currentBadgePopup?.badge_id) + 1} de {badgeQueue.length} badges
                </div>
              )}
              
              <Button 
                onClick={showNextBadge}
                className="btn-cockpit w-full"
                data-testid="badge-popup-continue-btn"
              >
                {badgeQueue.findIndex(b => b.badge_id === currentBadgePopup?.badge_id) < badgeQueue.length - 1 
                  ? 'Próximo Badge' 
                  : 'Continuar'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Edit Name Dialog */}
        <Dialog open={showEditNameDialog} onOpenChange={setShowEditNameDialog}>
          <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-chivo text-cockpit-text-bright flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-orange-400" />
                Editar Nome
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name" className="text-slate-300">Nome</Label>
                <Input
                  id="edit-name"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  placeholder="O seu nome"
                  className="bg-slate-800 border-slate-700 text-white"
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowEditNameDialog(false)}
                className="border-slate-700 text-slate-300"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveName}
                disabled={savingName || !editingName.trim()}
                className="btn-cockpit"
              >
                {savingName ? "A guardar..." : "Guardar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* School picker dialog (Phase 4 multi-tenancy) */}
        <SelectSchoolDialog
          open={showSchoolPicker}
          onOpenChange={setShowSchoolPicker}
          onSubmitted={() => checkAuth()}
        />
      </main>
    </div>
  );
};

const StatCard = ({ icon, value, label, color, onClick }) => {
  const colorClasses = {
    orange: "text-orange-400 bg-orange-500/10",
    emerald: "text-emerald-400 bg-emerald-500/10",
    cyan: "text-cyan-400 bg-cyan-500/10",
    yellow: "text-yellow-400 bg-yellow-500/10",
    purple: "text-purple-400 bg-purple-500/10",
  };

  return (
    <Card 
      className={`instrument-panel ${onClick ? 'cursor-pointer hover:border-slate-600 transition-colors' : ''}`}
      onClick={onClick}
    >
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <div className={`font-mono text-2xl ${colorClasses[color].split(' ')[0]}`}>{value}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
};

const ProgressItem = ({ section, onStartTraining }) => {
  const getStatusColor = (accuracy) => {
    if (accuracy >= 75) return "success";
    if (accuracy >= 50) return "warning";
    return "danger";
  };

  const getMasteryLabel = (accuracy, answered) => {
    if (answered === 0) return { text: "Por começar", color: "text-slate-400" };
    if (section.maintenance_mode) return { text: "Manutenção", color: "text-cyan-400" };
    if (accuracy >= 90) return { text: "Dominado", color: "text-emerald-400" };
    if (accuracy >= 75) return { text: "Bom", color: "text-emerald-400" };
    if (accuracy >= 50) return { text: "Em progresso", color: "text-yellow-400" };
    if (accuracy >= 25) return { text: "A melhorar", color: "text-orange-400" };
    return { text: "Iniciante", color: "text-orange-400" };
  };

  const masteryInfo = getMasteryLabel(section.accuracy, section.answered);
  const [showDetails, setShowDetails] = useState(false);

  const handleClick = () => {
    if (onStartTraining) {
      onStartTraining(section.section_id, section.section_name);
    }
  };

  return (
    <div className="bg-slate-900/50 rounded-lg border border-slate-800 hover:border-orange-500/50 transition-colors group">
  
  
  
      <div 
        className="p-3 sm:p-4 cursor-pointer"
        onClick={handleClick}
        title={`Clique para treinar ${section.section_name}`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2 mb-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 min-w-0">
            {section.caderno_name && (
              <span className="text-[10px] sm:text-xs bg-slate-800 text-slate-400 px-1.5 sm:px-2 py-0.5 rounded w-fit">
                {section.caderno_name}
              </span>
            )}
            <span className="text-xs sm:text-sm text-cockpit-text-bright group-hover:text-orange-400 transition-colors">
              {section.section_name}
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`text-[10px] sm:text-xs ${masteryInfo.color}`}>{masteryInfo.text}</span>
            {section.maintenance_mode && (
              <span className="text-[10px] bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 px-1.5 py-0.5 rounded-full font-mono">
                MODO MANUTENÇÃO
              </span>
            )}
            <span className={`font-mono text-xs sm:text-sm font-bold ${
              section.accuracy >= 75 ? 'text-emerald-400' : 
              section.accuracy >= 50 ? 'text-yellow-400' : 'text-red-400'
            }`}>
              {formatPercent(section.accuracy)}%
            </span>
            <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-orange-400 transition-colors" />
          </div>
        </div>
        <div className="progress-segmented">
          {[...Array(10)].map((_, i) => (
            <div 
              key={i} 
              className={`progress-segment ${i < Math.round(section.accuracy / 10) ? getStatusColor(section.accuracy) : ''}`}
            />
          ))}
        </div>
        <div className="flex justify-between mt-2 text-[10px] sm:text-xs text-slate-500 font-mono">
          <span>{section.answered > 0 ? `${section.correct} dominadas` : "Não iniciado"}</span>
          <span>{section.answered}/{section.total_questions} tentadas</span>
        </div>
      </div>
      {/* Toggle details */}
      {section.answered > 0 && (
        <div className="px-3 sm:px-4 pb-3">
          <button 
            onClick={(e) => { e.stopPropagation(); setShowDetails(!showDetails); }}
            className="text-[10px] text-slate-500 hover:text-slate-300 transition-colors"
            data-testid="toggle-details"
          >
            {showDetails ? '▲ Ocultar detalhes' : '▼ Ver detalhes'}
          </button>
          {showDetails && (
            <div className="mt-2 grid grid-cols-3 gap-2" data-testid="progress-details">
              <div className="bg-slate-800/50 rounded-lg p-2 text-center">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Cobertura</div>
                <div className="font-mono text-sm font-bold text-blue-400">{section.coverage ?? 0}%</div>
                <div className="text-[9px] text-slate-500 mt-0.5">
                  {section.answered}/{section.total_questions} vistas
                </div>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-2 text-center">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Domínio</div>
                <div className="font-mono text-sm font-bold text-orange-400">{section.mastery ?? 0}%</div>
                <div className="text-[9px] text-slate-500 mt-0.5">
                  {section.correct} dominadas
                </div>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-2 text-center">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Estabilidade</div>
                <div className="font-mono text-sm font-bold text-emerald-400">{section.stability ?? 0}%</div>
                <div className="text-[9px] text-slate-500 mt-0.5">
                  {section.correct > 0 ? 'streak 3+' : '—'}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const BadgeIcon = ({ badge, onNavigate }) => {
  const Icon = badgeIconMap[badge.icon] || Award;

  return (
    <div 
      className="aspect-square bg-gradient-to-b from-slate-700 to-slate-800 rounded-lg border border-slate-600 flex items-center justify-center group hover:border-orange-500/50 transition-colors cursor-pointer"
      title={`${badge.name} - Clique para ver detalhes`}
      onClick={onNavigate}
    >
      <Icon className="w-6 h-6 text-orange-400 group-hover:scale-110 transition-transform" />
    </div>
  );
};

export default StudentDashboard;
